import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { useCompanyStore } from "../companyStore/companyStore";
import { timeReportsV2Actions } from "./timeReportsV2Store.actions";
import {
  timeReportsV2InitState,
  type TimeReportsV2Actions,
  type TimeReportsV2State,
  timeReportsV2StateReducer,
} from "./timeReportsV2Store.reducer";

export type TimeReportsV2Dispatch = (action: TimeReportsV2Actions) => void;

const TimeReportsV2StateContext = createContext<TimeReportsV2State | undefined>(
  undefined,
);
const TimeReportsV2DispatchContext = createContext<
  TimeReportsV2Dispatch | undefined
>(undefined);

const COUNT_POLL_INTERVAL_MS = 15 * 60 * 1000;

export const TimeReportsV2StoreProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(
    timeReportsV2StateReducer,
    timeReportsV2InitState,
  );
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const actions = useMemo(() => timeReportsV2Actions(dispatch), [dispatch]);

  useEffect(() => {
    if (!companyId) return;
    void actions.refreshCount(companyId);

    const intervalId = window.setInterval(() => {
      void actions.refreshCount(companyId);
    }, COUNT_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [actions, companyId]);

  return (
    <TimeReportsV2StateContext.Provider value={state}>
      <TimeReportsV2DispatchContext.Provider value={dispatch}>
        {children}
      </TimeReportsV2DispatchContext.Provider>
    </TimeReportsV2StateContext.Provider>
  );
};

export const useTimeReportsV2Store = () => {
  const state = useContext(TimeReportsV2StateContext);
  const dispatch = useContext(TimeReportsV2DispatchContext);

  if (state === undefined || dispatch === undefined) {
    throw new Error(
      "useTimeReportsV2Store must be used within a TimeReportsV2StoreProvider",
    );
  }

  const actions = useMemo(() => timeReportsV2Actions(dispatch), [dispatch]);
  return [state, actions, dispatch] as const;
};
